import { useMutation, useQueryClient } from '@tanstack/react-query';

import API from 'services/api';
import FormSubmitError from '../../errors/FormSubmitError';
import { useTicket } from './index';

interface AddTicketParams<T = any> {
  ticketId: number;
  ticketComment: string;
  meta: T;
}

interface SaveCommentResponse {}

const addTicket = async <T = any>({ ticketId, ticketComment }: AddTicketParams<T>) => {
  try {
    return (await API.post<SaveCommentResponse>(`/ticket/${ticketId}/comment`, { ticket_comment: ticketComment })).data;
  } catch (e) {
    throw new FormSubmitError('An error occurred while sending request to the server. Please try again.');
  }
};

interface UseAddTicketCommentMutationProps<T> {
  onSuccess?: (data: SaveCommentResponse, variables: AddTicketParams<T>) => Promise<void>;
  onError?: (error: Error, variables: AddTicketParams<T>) => Promise<void>;
}

const useAddTicketCommentMutation = <T = any>({ onSuccess, onError }: UseAddTicketCommentMutationProps<T>) => {
  const queryClient = useQueryClient();

  return useMutation<SaveCommentResponse, FormSubmitError, AddTicketParams<T>>(addTicket, {
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: useTicket.getQueryKey(variables.ticketId) });
      await queryClient.refetchQueries({ queryKey: useTicket.getQueryKey(variables.ticketId) });

      if (onSuccess) {
        await onSuccess(data, variables);
      }
    },
    onError: async (error: Error, variables) => {
      if (onError) {
        await onError(error, variables);
      }
    },
  });
};

useAddTicketCommentMutation.queries = {
  addTicket,
} as const;

export default useAddTicketCommentMutation;
